import { BREAKPOINTS, COLORS, LoadingIndicator } from "@laerdal/life-react-components";
import axios from "axios";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import posthog from 'posthog-js';

import ProductApi from "../../services/api/ProductApi";
import { ProductLineDetailsDto } from "../../model/dto/productLineDetailsDto";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import { buildMarketPath, selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { marketList } from "../../model/constants/marketContants";
import PaligoContent from "../../commonComponents/PaligoContent";

const USER_GUIDE_URL = "https://cltstorageprod.blob.core.windows.net/paligo/processed/53614-Paul_User_Guide-html_-Paul_CLT_1771929333.0757/en/index-en.html";

// Extracts the <body> innerHTML from a full HTML document and strips out
// <style>, <link> and <script> tags so the Paligo CSS does not leak into
// (and break the layout of) the host application.
const extractBodyContent = (raw: string): string => {
    try {
        const doc = new DOMParser().parseFromString(raw, 'text/html');
        doc.querySelectorAll('style, link, script').forEach((node) => node.remove());
        return doc.body?.innerHTML ?? raw;
    } catch {
        return raw;
    }
};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    ${BREAKPOINTS.MEDIUM}{
        gap: 48px;
    }
`;

const BreadcrumbContainer = styled.div`
    background: ${COLORS.neutral_20};
    border-bottom: 1px solid #CCC;
`;

const Breadcrumb = styled.div`
    padding: 12px 0;
    margin-top: 32px;
`;

const CrumbLink = styled.a`
    &:link, &:visited, &:hover, &:active {
        color: ${COLORS.primary_600};
        text-decoration: none;
    }

    color: ${COLORS.primary_600};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 25px;
`;

const CrumbText = styled.span`
    color: ${COLORS.neutral_500};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 25px;
`;

const Divider = styled.span`
    padding: 0 10px;
    color: ${COLORS.neutral_500};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 25px;
`;



const LoadingContainer = styled.div`
    padding: 64px 0;
    display: flex;
    justify-content: center;
`;

interface Crumb {
    text: string;
    link?: string;
}

const ProductLineUserGuidePage3 = () => {
    const { categoryLink, productLink } = useParams<{ categoryLink: string, productLink: string }>();
    const { t } = useTranslation("landingPage");
    const navigate = useNavigate();

    const currentMartket = useSelector(selectMarket);
    const translateWithAi = useSelector(selectTranslateWithAi);

    const [productLineLoading, setProductLineLoading] = useState<boolean>(true);
    const [productLine, setProductLine] = useState<ProductLineDetailsDto | undefined>(undefined);

    const [userGuideLoading, setUserGuideLoading] = useState<boolean>(true);
    const [userGuideHtml, setUserGuideHtml] = useState<string>('');

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;
        setProductLineLoading(true);
        setProductLine(undefined);

        if (categoryLink && productLink) {
            ProductApi
                .GetProductLine(productLink, (categoryLink != 'medical-devices' && translateWithAi) ? marketList[currentMartket].locale : 'en', signal)
                .then((data) => {
                    setProductLineLoading(false);
                    setProductLine(data);
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                });
        }

        return () => {
            controller.abort();
        };
    }, [categoryLink, productLink, translateWithAi, currentMartket]);

    useEffect(() => {
        const controller = new AbortController();
        setUserGuideLoading(true);
        setUserGuideHtml('');

        axios
            .get(USER_GUIDE_URL, { responseType: 'text', signal: controller.signal })
            .then((response) => {
                const raw = typeof response.data === 'string' ? response.data : String(response.data);
                setUserGuideHtml(extractBodyContent(raw));
                setUserGuideLoading(false);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                setUserGuideLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, []);

    const breadcrumb: Crumb[] = [
        {
            text: t('Home'),
            link: `/${currentMartket}`
        },
        {
            text: productLine ? t(productLine.categoryName) : '',
            link: buildMarketPath(currentMartket, `/category/${categoryLink}`)
        },
        {
            text: productLine?.displayName ?? '',
            link: buildMarketPath(currentMartket, `/category/${categoryLink}/product/${productLink}`)
        },
        {
            text: t('User guide')
        }
    ];

    return (
        <Wrapper>
            <BreadcrumbContainer>
                <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
                    <Breadcrumb>
                        {breadcrumb.map((item, index) => (
                            <span key={`breadcrumb-${index}`}>
                                {!!item.link &&
                                    <CrumbLink
                                        href={item.link}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            posthog.capture('SUPP-BreadcrumbClick', {
                                                currentUrl: window.location.href,
                                                clickedLink: item.link,
                                                step: 1
                                            });
                                            navigate(item.link!);
                                        }}
                                    >
                                        {item.text}
                                    </CrumbLink>
                                }
                                {!item.link &&
                                    <CrumbText>{item.text}</CrumbText>
                                }
                                {(breadcrumb.length != index + 1) &&
                                    <Divider>/</Divider>
                                }
                            </span>
                        ))}
                    </Breadcrumb>
                </PageWidthFull>
            </BreadcrumbContainer>

            <PageWidthFull $useMaxWidth={true} $maxWidth={1024} $margin="0 auto">
                {(productLineLoading || userGuideLoading) &&
                    <LoadingContainer>
                        <LoadingIndicator />
                    </LoadingContainer>
                }

                {!userGuideLoading && userGuideHtml &&
                    <PaligoContent dangerouslySetInnerHTML={{ __html: userGuideHtml }} />
                }
            </PageWidthFull>
        </Wrapper>
    );
};

export default ProductLineUserGuidePage3;
